
import { DotNetModule } from '../types';

export const DOTNET_INTRODUCTION_MODULE: DotNetModule = {
  id: "dotnet-introduction",
  title: "Introduction to .NET Framework vs .NET Core",
  description: "Understand the evolution and differences between .NET Framework and .NET Core/.NET 5+",
  difficulty: "beginner",
  content: `
# Introduction to .NET Framework vs .NET Core

## What is .NET?
.NET is a free, open-source developer platform for building many different types of applications.

## .NET Framework vs .NET Core
Understanding the key differences between traditional .NET Framework and modern .NET Core/.NET 5+.

### .NET Framework
- Windows-only platform
- Mature ecosystem with extensive libraries
- Desktop and web applications

### .NET Core/.NET 5+
- Cross-platform (Windows, macOS, Linux)
- Modern, high-performance runtime
- Cloud-native applications

## Choosing the Right Platform
Learn when to use each platform based on your project requirements.
  `,
  duration: "45 minutes",
  order: 1,
  objectives: [
    "Understand .NET ecosystem overview",
    "Distinguish between .NET Framework and .NET Core",
    "Choose appropriate platform for projects"
  ],
  quiz: {
    questions: [
      {
        id: "q1",
        question: "What is the main advantage of .NET Core over .NET Framework?",
        options: [
          "Better performance only",
          "Cross-platform compatibility",
          "More libraries available",
          "Easier to learn"
        ],
        correctAnswer: 1,
        explanation: ".NET Core's main advantage is cross-platform compatibility, allowing applications to run on Windows, macOS, and Linux."
      }
    ]
  }
};

export const DOTNET_SETUP_MODULE: DotNetModule = {
  id: "dotnet-setup",
  title: "Setting up Visual Studio and .NET SDK",
  description: "Install and configure your .NET development environment",
  difficulty: "beginner",
  content: `
# Setting up Visual Studio and .NET SDK

## Installing .NET SDK
Download and install the latest .NET SDK from the official Microsoft website.

## Visual Studio Setup
Set up Visual Studio Community for .NET development with essential extensions.

## Alternative IDEs
Explore other development environments like Visual Studio Code and JetBrains Rider.

## First Project Setup
Create your first .NET console application and understand the project structure.
  `,
  duration: "30 minutes",
  order: 2,
  objectives: [
    "Install .NET SDK and Visual Studio",
    "Configure development environment",
    "Create first .NET project"
  ]
};

export const DOTNET_CSHARP_BASICS_MODULE: DotNetModule = {
  id: "dotnet-csharp-basics",
  title: "C# Syntax, Variables, Data Types, Control Structures",
  description: "Master fundamental C# programming concepts and syntax",
  difficulty: "beginner",
  content: `
# C# Syntax, Variables, Data Types, Control Structures

## Variables and Data Types
Learn about C#'s type system and how to declare variables.

## Control Structures
Master if statements, loops, and switch expressions.

## Methods and Functions
Understand how to create and use methods in C#.

## Exception Handling
Learn basic error handling with try-catch blocks.
  `,
  duration: "90 minutes",
  order: 3,
  objectives: [
    "Understand C# syntax and conventions",
    "Work with variables and data types",
    "Implement control flow structures",
    "Handle basic exceptions"
  ],
  practice: {
    task: "Create a C# program that calculates the factorial of a number using loops",
    starter: `using System;

class Program
{
    static void Main()
    {
        Console.WriteLine("Enter a number:");
        // Your code here
    }
}`,
    solution: `using System;

class Program
{
    static void Main()
    {
        Console.WriteLine("Enter a number:");
        int number = int.Parse(Console.ReadLine());
        
        long factorial = 1;
        for (int i = 1; i <= number; i++)
        {
            factorial *= i;
        }
        
        Console.WriteLine($"Factorial of {number} is {factorial}");
    }
}`,
    hints: [
      "Use a for loop to multiply numbers",
      "Consider using long for larger factorials",
      "Don't forget to parse the input string to integer"
    ]
  }
};

export const DOTNET_OOP_MODULE: DotNetModule = {
  id: "dotnet-oop",
  title: "OOP in C# (Classes, Inheritance, Polymorphism)",
  description: "Learn object-oriented programming principles in C#",
  difficulty: "beginner",
  content: `
# Object-Oriented Programming in C#

## Classes and Objects
Understand the fundamental building blocks of OOP in C#.

## Inheritance
Learn how to create class hierarchies and reuse code.

## Polymorphism
Master method overriding and interface implementation.

## Encapsulation
Understand access modifiers and data protection.
  `,
  duration: "120 minutes",
  order: 4,
  objectives: [
    "Create classes and objects",
    "Implement inheritance hierarchies",
    "Use polymorphism effectively",
    "Apply encapsulation principles"
  ]
};

export const DOTNET_CONSOLE_APPS_MODULE: DotNetModule = {
  id: "dotnet-console-apps",
  title: "Basic Console Applications",
  description: "Build interactive console applications in C#",
  difficulty: "beginner",
  content: `
# Basic Console Applications

## Console Input/Output
Master reading from and writing to the console.

## Command Line Arguments
Learn to process command line parameters.

## File Operations
Basic file reading and writing operations.

## Configuration and Settings
Handle application configuration and user preferences.
  `,
  duration: "75 minutes",
  order: 5,
  objectives: [
    "Handle console input and output",
    "Process command line arguments",
    "Perform basic file operations",
    "Manage application configuration"
  ]
};

export const DOTNET_ERROR_HANDLING_MODULE: DotNetModule = {
  id: "dotnet-error-handling",
  title: "Error Handling & Debugging",
  description: "Learn effective error handling and debugging techniques",
  difficulty: "beginner",
  content: `
# Error Handling & Debugging

## Exception Handling
Master try-catch-finally blocks and custom exceptions.

## Debugging Techniques
Learn to use Visual Studio debugger effectively.

## Logging
Implement proper logging practices in your applications.

## Testing and Validation
Basic unit testing and input validation techniques.
  `,
  duration: "60 minutes",
  order: 6,
  objectives: [
    "Handle exceptions properly",
    "Use debugging tools effectively",
    "Implement logging strategies",
    "Validate user input"
  ]
};

export const DOTNET_CALCULATOR_PROJECT_MODULE: DotNetModule = {
  id: "dotnet-calculator-project",
  title: "First Project: Create a Calculator Console App",
  description: "Apply your knowledge to build a complete calculator application",
  difficulty: "beginner",
  content: `
# Calculator Console Application Project

## Project Requirements
Build a console calculator that supports basic arithmetic operations.

## Implementation Steps
Step-by-step guide to building your calculator application.

## Code Organization
Learn to structure your code using classes and methods.

## Testing and Validation
Test your calculator with various inputs and edge cases.
  `,
  duration: "90 minutes",
  order: 7,
  objectives: [
    "Apply C# fundamentals in a real project",
    "Organize code using OOP principles",
    "Handle user input and validation",
    "Test application thoroughly"
  ],
  practice: {
    task: "Build a complete calculator console application",
    starter: `using System;

class Calculator
{
    static void Main()
    {
        Console.WriteLine("Welcome to Calculator!");
        // Implement your calculator here
    }
}`,
    solution: `using System;

class Calculator
{
    static void Main()
    {
        Console.WriteLine("Welcome to Calculator!");
        
        while (true)
        {
            Console.WriteLine("\\nEnter first number (or 'exit' to quit):");
            string input1 = Console.ReadLine();
            
            if (input1.ToLower() == "exit")
                break;
                
            Console.WriteLine("Enter operator (+, -, *, /):");
            string op = Console.ReadLine();
            
            Console.WriteLine("Enter second number:");
            string input2 = Console.ReadLine();
            
            try
            {
                double num1 = double.Parse(input1);
                double num2 = double.Parse(input2);
                double result = Calculate(num1, num2, op);
                
                Console.WriteLine($"Result: {result}");
            }
            catch (Exception ex)
            {
                Console.WriteLine($"Error: {ex.Message}");
            }
        }
    }
    
    static double Calculate(double a, double b, string operation)
    {
        return operation switch
        {
            "+" => a + b,
            "-" => a - b,
            "*" => a * b,
            "/" => b != 0 ? a / b : throw new DivideByZeroException(),
            _ => throw new ArgumentException("Invalid operator")
        };
    }
}`,
    hints: [
      "Use a loop for continuous operation",
      "Handle division by zero",
      "Use switch expressions for cleaner code",
      "Add proper error handling for invalid inputs"
    ],
    estimatedTime: 90
  }
};

export const DOTNET_BASIC_MODULES: DotNetModule[] = [
  DOTNET_INTRODUCTION_MODULE,
  DOTNET_SETUP_MODULE,
  DOTNET_CSHARP_BASICS_MODULE,
  DOTNET_OOP_MODULE,
  DOTNET_CONSOLE_APPS_MODULE,
  DOTNET_ERROR_HANDLING_MODULE,
  DOTNET_CALCULATOR_PROJECT_MODULE
];
